import { ArrowsClockwise } from '@phosphor-icons/react';
import type { ColumnDef } from '@tanstack/react-table';
import type React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from '@/providers/RouterShim';
import useBranches from '../../../../../data/branch/useBranches';
import type { BranchHierachy } from '../../../../../data/branch/useBranchHierarchy';
import type {
  VehicleExplore,
  VehicleExploreSalesOrder,
} from '../../../../../data/vehicles/useVehicleExplore';
import Badge from '../../../../../styled-components/components/Badge';
import { HStack } from '../../../../../styled-components/components/HStack';
import { Link } from '../../../../../styled-components/components/Link';
import { LinkButton } from '../../../../../styled-components/components/LinkButton';
import { formatYYYYMMDDLocale, useDateLocale } from '../../../../../utils/date';
import { ProgressStateTypesBadge } from '../../../Item/Shared/StatusInformationAside/ProgressState/ProgressStatesHelpers';
import { resolveBranch } from '../../utils';
import TaskStatePresenter from './TaskStatePresenter';

const ns = 'salesAndPurchasingVehicles';

const COMPLTED = ['urn:pf:task:state:completed'];
const IN_PROGRESS = ['urn:pf:task:state:requires-approval', 'urn:pf:task:state:started'];
const NOT_STARTED = ['urn:pf:task:state:pending', 'urn:pf:task:state:initializing'];
const FAILED = [
  'urn:pf:task:state:not-applicable',
  'urn:pf:task:state:canceled',
  'urn:pf:task:state:failed',
];

interface UseExistingVehiclesColumnsProps {
  branchHierachy: BranchHierachy | undefined;
  context?: 'order';
  setSelectedVehicle: React.Dispatch<React.SetStateAction<VehicleExplore | null>>;
  onSelectVehicle?: (vehicle: VehicleExplore) => void;
}

const useExistingVehiclesColumns = ({
  branchHierachy,
  context,
  setSelectedVehicle,
  onSelectVehicle,
}: UseExistingVehiclesColumnsProps) => {
  const dateLocale = useDateLocale();
  const { branchId } = useParams();
  const [branches] = useBranches();
  const { t } = useTranslation([
    ns,
    'validation',
    'common',
    'glossary',
    'salesAndPurchasingVehicles',
  ]);

  const getBranchName = (vehicle: VehicleExplore) => {
    return (
      resolveBranch(vehicle.branchId, branchHierachy) || t('existingVehicles.unknownBranch', { ns })
    );
  };

  const columns = useMemo<ColumnDef<VehicleExplore>[]>(() => {
    if (context === 'order') {
      return [
        {
          id: 'action',
          header: t('link', { ns: 'glossary' }),
          meta: { headerAsString: t('link', { ns: 'glossary' }) },
          cell: ({ row }) => {
            const vehicle = row.original;
            const hasAccessToVehicle = (branches || [])
              .map((branch) => branch.id)
              .includes(vehicle.branchId);
            const isOnSameBranch = branchId === vehicle.branchId;

            if (!hasAccessToVehicle) {
              return (
                <Badge tone="disabled">
                  {t('noAdminAccess', { ns: 'salesAndPurchasingVehicles' })}
                </Badge>
              );
            }

            const path = `/${vehicle.branchId}/vehicles/administrate-vehicles/${vehicle.id}`;
            const fullUrl = `${window.location.origin}${path}`;

            return (
              <Link href={fullUrl} target="_blank" rel="noopener noreferrer">
                {isOnSameBranch ? (
                  t('goToVehicle', { ns: 'salesAndPurchasingVehicles' })
                ) : (
                  <>
                    <ArrowsClockwise size={16} weight="bold" />
                    {t('switchBranchAndGoToVehicle', {
                      ns: 'salesAndPurchasingVehicles',
                    })}
                  </>
                )}
              </Link>
            );
          },
        },
        {
          accessorKey: 'branchId',
          header: t('existingVehicles.fields.branch', { ns }),
          meta: { headerAsString: t('existingVehicles.fields.branch', { ns }) },
          cell: ({ row }) => {
            const vehicle = row.original;
            const branchName = getBranchName(vehicle);
            return branchName;
          },
        },
        {
          accessorKey: 'ad.price',
          header: t('existingVehicles.fields.adPrice', { ns }),
          meta: { headerAsString: t('existingVehicles.fields.adPrice', { ns }) },
          cell: ({ row }) => {
            const price = row.original.ad?.price;
            return price ? `${price.toLocaleString('sv-SE')} kr` : '-';
          },
        },
        {
          accessorFn: (vehicle) => vehicle.sales?.hasOrders || false,
          header: t('existingVehicles.fields.hasOrders', { ns }),
          meta: { headerAsString: t('existingVehicles.fields.hasOrders', { ns }) },
          cell: ({ getValue }) => {
            const hasOrder = getValue<boolean>();
            return hasOrder ? t('yes', { ns: 'common' }) : t('no', { ns: 'common' });
          },
        },
        {
          accessorFn: (vehicle) => vehicle.sales?.orders,
          header: t('existingVehicles.fields.orders', { ns }),
          meta: { headerAsString: t('existingVehicles.fields.orders', { ns }) },
          cell: ({ getValue }) => {
            const orders = getValue<VehicleExploreSalesOrder[] | undefined>();
            const ordersCount = orders?.length || 0;
            return ordersCount;
          },
        },
        {
          accessorFn: (vehicle) => vehicle.sales?.isReserved,
          header: t('existingVehicles.fields.isReserved', { ns }),
          meta: { headerAsString: t('existingVehicles.fields.isReserved', { ns }) },
          cell: ({ getValue }) => {
            const isReserved = getValue<boolean>();
            return isReserved ? t('yes', { ns: 'common' }) : t('no', { ns: 'common' });
          },
        },
        ...(onSelectVehicle
          ? [
              {
                id: 'selectVehicle',
                header: '',
                meta: { headerAsString: '' },
                cell: ({ row }: { row: { original: VehicleExplore } }) => {
                  const vehicle = row.original;
                  return (
                    <LinkButton onClick={() => onSelectVehicle(vehicle)}>
                      {t('existingVehicles.addToOrder', { ns })}
                    </LinkButton>
                  );
                },
              } as ColumnDef<VehicleExplore>,
            ]
          : []),
      ];
    }

    return [
      {
        id: 'action',
        header: t('link', { ns: 'glossary' }),
        meta: { headerAsString: t('link', { ns: 'glossary' }) },
        cell: ({ row }) => {
          const vehicle = row.original;
          const hasAccessToVehicle = (branches || [])
            .map((branch) => branch.id)
            .includes(vehicle.branchId);
          const isOnSameBranch = branchId === vehicle.branchId;

          if (!hasAccessToVehicle) {
            return (
              <Badge tone="disabled">
                {t('noAdminAccess', { ns: 'salesAndPurchasingVehicles' })}
              </Badge>
            );
          }

          const path = `/${vehicle.branchId}/vehicles/administrate-vehicles/${vehicle.id}`;
          const fullUrl = `${window.location.origin}${path}`;

          return (
            <Link href={fullUrl} target="_blank" rel="noopener noreferrer">
              {isOnSameBranch ? (
                t('goToVehicle', { ns: 'salesAndPurchasingVehicles' })
              ) : (
                <>
                  <ArrowsClockwise size={16} weight="bold" />
                  {t('switchBranchAndGoToVehicle', {
                    ns: 'salesAndPurchasingVehicles',
                  })}
                </>
              )}
            </Link>
          );
        },
      },
      {
        accessorKey: 'branchId',
        header: t('existingVehicles.fields.branch', { ns }),
        meta: { headerAsString: t('existingVehicles.fields.branch', { ns }) },
        cell: ({ row }) => {
          const vehicle = row.original;
          const branchName = getBranchName(vehicle);
          return branchName;
        },
      },
      {
        accessorKey: 'createdAt',
        header: t('existingVehicles.fields.createdAt', { ns }),
        meta: { headerAsString: t('existingVehicles.fields.createdAt', { ns }) },
        cell: ({ getValue }) => formatYYYYMMDDLocale(getValue<string>(), dateLocale),
      },
      {
        accessorKey: 'lastUpdatedAt',
        header: t('existingVehicles.fields.updatedAt', { ns }),
        meta: { headerAsString: t('existingVehicles.fields.updatedAt', { ns }) },
        cell: ({ getValue }) => formatYYYYMMDDLocale(getValue<string>(), dateLocale),
      },
      {
        accessorKey: 'progressState',
        header: t('existingVehicles.fields.progress', { ns }),
        meta: { headerAsString: t('existingVehicles.fields.progress', { ns }) },
        cell: ({ getValue }) => {
          const progressState = getValue() as VehicleExplore['progressState'];
          return progressState?.type ? <ProgressStateTypesBadge type={progressState.type} /> : '-';
        },
      },
      {
        accessorKey: 'purchaseInfo.status',
        header: t('existingVehicles.fields.status', { ns }),
        meta: { headerAsString: t('existingVehicles.fields.status', { ns }) },
        cell: ({ row }) => {
          const status = (row.original.purchaseInfo as any)?.status;
          if (!status) return '-';

          let statusText: string;
          let tone: 'success' | 'warning' | 'info';

          switch (status) {
            case 'CONFIRMED':
            case 'statusConfirmed':
              statusText = t('existingVehicles.statusConfirmed', { ns });
              tone = 'success';
              break;
            case 'PENDING':
            case 'statusPending':
              statusText = t('existingVehicles.statusPending', { ns });
              tone = 'info';
              break;
            default:
              statusText = t('existingVehicles.statusDraft', { ns });
              tone = 'warning';
              break;
          }

          return <Badge tone={tone}>{statusText}</Badge>;
        },
      },
      {
        accessorKey: 'purchaseInfo.price',
        header: t('existingVehicles.fields.purchasePrice', { ns }),
        meta: { headerAsString: t('existingVehicles.fields.purchasePrice', { ns }) },
        cell: ({ row }) => {
          const price = row.original.purchaseInfo?.price;
          return price ? `${price.toLocaleString('sv-SE')} kr` : '-';
        },
      },
      {
        accessorKey: 'purchaseInfo.costOfRepair',
        header: t('existingVehicles.fields.costOfRepair', { ns }),
        meta: { headerAsString: t('existingVehicles.fields.costOfRepair', { ns }) },
        cell: ({ row }) => {
          const cost = row.original.purchaseInfo?.costOfRepair;
          return cost ? `${cost.toLocaleString('sv-SE')} kr` : '-';
        },
      },
      {
        accessorKey: 'purchaseInfo.marketValue',
        header: t('existingVehicles.fields.marketValue', { ns }),
        meta: { headerAsString: t('existingVehicles.fields.marketValue', { ns }) },
        cell: ({ row }) => {
          const value = (row.original.purchaseInfo as any)?.marketValue;
          return value ? `${value.toLocaleString('sv-SE')} kr` : '-';
        },
      },
      {
        accessorKey: 'ad.price',
        header: t('existingVehicles.fields.adPrice', { ns }),
        meta: { headerAsString: t('existingVehicles.fields.adPrice', { ns }) },
        cell: ({ row }) => {
          const price = row.original.ad?.price;
          return price ? `${price.toLocaleString('sv-SE')} kr` : '-';
        },
      },
      {
        accessorKey: 'publishedAt',
        header: t('existingVehicles.fields.published', { ns }),
        meta: { headerAsString: t('existingVehicles.fields.published', { ns }) },
        cell: ({ getValue }) => {
          const value = getValue<string | null | undefined>();
          return value ? formatYYYYMMDDLocale(value, dateLocale) : '-';
        },
      },
      {
        accessorKey: 'tasks',
        header: t('existingVehicles.fields.processStep', { ns }),
        meta: { headerAsString: t('existingVehicles.fields.processStep', { ns }) },
        cell: ({ row }) => {
          const vehicle = row.original;
          if (!vehicle.tasks?.length) return '-';

          const completed = vehicle.tasks.filter((x) => COMPLTED.includes(x.state)).length;
          const inProgress = vehicle.tasks.filter((x) => IN_PROGRESS.includes(x.state)).length;
          const notStarted = vehicle.tasks.filter((x) => NOT_STARTED.includes(x.state)).length;
          const failed = vehicle.tasks.filter((x) => FAILED.includes(x.state)).length;

          return (
            <HStack $valign="center" $spacing={1}>
              <TaskStatePresenter
                completed={completed}
                inProgress={inProgress}
                notStarted={notStarted}
                problem={failed}
              />
              <LinkButton onClick={() => setSelectedVehicle(vehicle)}>
                {t('existingVehicles.fields.tasks.details', { ns })}
              </LinkButton>
            </HStack>
          );
        },
      },
    ];
  }, [branchId, branches, branchHierachy, dateLocale, t, context, onSelectVehicle]);

  return columns;
};

export default useExistingVehiclesColumns;
